import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError } from "@/api/http";
import { webhooks, type WebhookEndpoint } from "@/api/platformAdmin";
import { useToast } from "@/components/Toast";
import { Badge, Button, Card, CardBody, CardHeader, EmptyState } from "@/components/ui";

function errMsg(e: unknown) {
  return e instanceof ApiError ? e.message.replace(/^\d+:\s*/, "") : String(e);
}

/**
 * Webhook endpoints + recent delivery attempts. Operators can disable a noisy
 * or failing endpoint; secrets are never exposed by the API.
 */
export function AdminWebhooksPage() {
  const endpoints = useQuery({ queryKey: ["webhooks"], queryFn: () => webhooks.list() });
  const deliveries = useQuery({
    queryKey: ["webhook-deliveries"],
    queryFn: () => webhooks.deliveries(),
    refetchInterval: 30_000,
  });
  const qc = useQueryClient();
  const toast = useToast();

  const toggle = useMutation({
    mutationFn: (w: WebhookEndpoint) => webhooks.setEnabled(w.id, !w.enabled),
    onSuccess: () => {
      toast.push("Endpoint updated", "success");
      qc.invalidateQueries({ queryKey: ["webhooks"] });
    },
    onError: (e) => toast.push(errMsg(e), "error"),
  });

  return (
    <div className="space-y-3">
      <h1 className="text-base font-semibold text-term-fg">Webhooks</h1>

      <Card>
        <CardHeader>
          <span className="text-sm font-medium">Endpoints</span>
        </CardHeader>
        <CardBody className="p-0">
          {endpoints.isLoading ? (
            <div className="p-3 text-sm text-term-mute">Loading…</div>
          ) : !endpoints.data || endpoints.data.length === 0 ? (
            <EmptyState title="No endpoints" description="Registered webhook endpoints appear here." />
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-term-elev text-term-mute">
                <tr>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">URL</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Events</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Last status</th>
                  <th className="px-3 py-1.5 text-right text-2xs uppercase tracking-wider">Fails</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">State</th>
                  <th className="px-3 py-1.5 text-right text-2xs uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {endpoints.data.map((w) => (
                  <tr key={w.id} className="border-t border-term-border hover:bg-term-elev">
                    <td className="px-3 py-1.5 font-mono text-2xs text-term-fg">{w.url}</td>
                    <td className="px-3 py-1.5 text-2xs text-term-dim">
                      {(w.event_types ?? []).join(", ") || "—"}
                    </td>
                    <td className="px-3 py-1.5 text-2xs text-term-dim">{w.last_status ?? "—"}</td>
                    <td className="px-3 py-1.5 text-right tnum text-term-dim">{w.consecutive_failures}</td>
                    <td className="px-3 py-1.5">
                      <Badge tone={w.enabled ? "good" : "neutral"}>{w.enabled ? "Enabled" : "Disabled"}</Badge>
                    </td>
                    <td className="px-3 py-1.5 text-right">
                      <Button variant="ghost" onClick={() => toggle.mutate(w)} disabled={toggle.isPending}>
                        {w.enabled ? "Disable" : "Enable"}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <span className="text-sm font-medium">Recent deliveries</span>
        </CardHeader>
        <CardBody className="p-0">
          {!deliveries.data || deliveries.data.length === 0 ? (
            <EmptyState title="No deliveries" description="Delivery attempts appear here." />
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-term-elev text-term-mute">
                <tr>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">When</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Event</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Status</th>
                  <th className="px-3 py-1.5 text-right text-2xs uppercase tracking-wider">HTTP</th>
                  <th className="px-3 py-1.5 text-right text-2xs uppercase tracking-wider">Attempts</th>
                </tr>
              </thead>
              <tbody>
                {deliveries.data.map((d) => (
                  <tr key={d.id} className="border-t border-term-border hover:bg-term-elev">
                    <td className="px-3 py-1.5 tnum text-term-dim whitespace-nowrap">
                      {d.created_at ? new Date(d.created_at).toLocaleString() : "—"}
                    </td>
                    <td className="px-3 py-1.5 text-2xs text-term-dim">{d.event_type}</td>
                    <td className="px-3 py-1.5">
                      <Badge tone={d.status === "delivered" ? "good" : d.status === "failed" ? "bad" : "warn"}>
                        {d.status}
                      </Badge>
                    </td>
                    <td className="px-3 py-1.5 text-right tnum text-term-dim">{d.response_status ?? "—"}</td>
                    <td className="px-3 py-1.5 text-right tnum text-term-dim">{d.attempt_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
